import { useEffect, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../utils/supabaseStorage';

/**
 * Reads `public.feature_flags` with a single shared Realtime subscription per page session.
 *
 * - Multiple components calling `useFeatureFlag('foo')` share the same cached entry
 *   and the same Postgres-changes subscription (no per-component channel cost).
 * - Initial value is fetched lazily on first mount per key; subsequent INSERT/UPDATE/DELETE
 *   on `feature_flags` propagate via Realtime.
 * - Closed-fail policy: while loading, `enabled` is `false` so flag-gated UI stays hidden.
 */

type FlagPayload = Record<string, unknown> | unknown[] | string | number | boolean | null;

interface FlagState {
  enabled: boolean;
  payload: FlagPayload;
  loaded: boolean;
}

const cache = new Map<string, FlagState>();
const subscribers = new Map<string, Set<() => void>>();
const inflight = new Map<string, Promise<void>>();

let realtimeChannel: RealtimeChannel | null = null;

function notify(key: string) {
  const set = subscribers.get(key);
  if (!set) return;
  for (const fn of set) fn();
}

function applyRow(row: { key?: string; enabled?: boolean; payload?: unknown } | null | undefined, deleted = false) {
  if (!row || typeof row.key !== 'string') return;
  const next: FlagState = deleted
    ? { enabled: false, payload: null, loaded: true }
    : {
        enabled: row.enabled === true,
        payload: (row.payload as FlagPayload) ?? null,
        loaded: true,
      };
  cache.set(row.key, next);
  notify(row.key);
}

function ensureRealtime() {
  if (realtimeChannel) return;
  realtimeChannel = supabase
    .channel('feature_flags:all')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'feature_flags' },
      (msg) => {
        const m = msg as { eventType?: string; event?: string; new?: unknown; old?: unknown };
        const eventType = m.eventType ?? m.event;
        if (eventType === 'DELETE') {
          applyRow(m.old as { key?: string }, true);
        } else {
          applyRow(m.new as { key?: string; enabled?: boolean; payload?: unknown });
        }
      }
    )
    .subscribe();
}

async function loadKey(key: string): Promise<void> {
  let p = inflight.get(key);
  if (p) return p;
  p = (async () => {
    try {
      const { data, error } = await supabase
        .from('feature_flags')
        .select('enabled, payload')
        .eq('key', key)
        .maybeSingle();
      if (error) {
        console.warn('[useFeatureFlag] fetch failed for', key, error.message);
      }
      cache.set(key, {
        enabled: data?.enabled === true,
        payload: (data?.payload as FlagPayload) ?? null,
        loaded: true,
      });
      notify(key);
    } finally {
      inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

export interface UseFeatureFlagResult {
  enabled: boolean;
  payload: FlagPayload;
  loading: boolean;
}

export function useFeatureFlag(key: string): UseFeatureFlagResult {
  const [, force] = useState(0);

  useEffect(() => {
    ensureRealtime();

    let set = subscribers.get(key);
    if (!set) {
      set = new Set();
      subscribers.set(key, set);
    }
    const tick = () => force((n) => (n + 1) | 0);
    set.add(tick);

    if (!cache.get(key)?.loaded) {
      void loadKey(key);
    }

    return () => {
      set!.delete(tick);
    };
  }, [key]);

  const entry = cache.get(key);
  return {
    enabled: entry?.enabled === true,
    payload: entry?.payload ?? null,
    loading: !entry?.loaded,
  };
}

/** Imperative read for places where hooks aren't available (e.g. router loaders). */
export function readFeatureFlag(key: string): UseFeatureFlagResult {
  const entry = cache.get(key);
  if (!entry?.loaded) {
    void loadKey(key);
  }
  return {
    enabled: entry?.enabled === true,
    payload: entry?.payload ?? null,
    loading: !entry?.loaded,
  };
}
